/* ═══════════════════════════════════════════════════════════════════════════
   Colourway picker

   A sticky image panel whose colourway changes when a lens dot is clicked:
   swaps the active shot, the name, description, price, the hidden variant
   input, and the accent colour the name is rendered in. Nothing here is
   scroll-driven — there is no scroll track, no scroll-position math, no
   auto-scaled block height. Below the CSS breakpoint the panel is static;
   above it, .lens-stick is sticky, but that's a plain CSS position, not
   something this script manages.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const H = window.Hystride;
  if (!H) return;

  H.register('colourway-scroller', {
    mount(root) {
      const stick = root.querySelector('[data-lens-stick]');
      // .on goes on the <img> itself — .lens-vis img.on is what reveals it.
      const shots = Array.from(root.querySelectorAll('.lens-vis img'));
      const dots = Array.from(root.querySelectorAll('[data-lens-dot]'));
      const nameEl = root.querySelector('[data-lens-name]');
      const descEl = root.querySelector('[data-lens-desc]');
      const idxEl = root.querySelector('[data-lens-index]');
      const buyLabel = root.querySelector('[data-lens-buy-label]');
      const buyPrice = root.querySelector('[data-lens-buy-price]');
      const variantInput = root.querySelector('[data-lens-variant-input]');
      const linkEl = root.querySelector('[data-lens-link]');

      const N = dots.length;
      if (N === 0) return null;

      let current = -1;

      const setColour = (i) => {
        if (i === current) return;
        current = i;

        const dot = dots[i];

        shots.forEach((s, n) => s.classList.toggle('on', n === i));
        dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false'));

        if (nameEl) nameEl.textContent = dot.dataset.name;
        if (descEl) descEl.textContent = dot.dataset.note || '';
        if (idxEl) {
          idxEl.textContent = `${String(i + 1).padStart(2, '0')} / ${String(N).padStart(2, '0')}`;
        }
        if (buyLabel) {
          buyLabel.textContent = `${buyLabel.dataset.prefix || buyLabel.textContent.split(' ')[0]} ${dot.dataset.name}`;
        }
        if (buyPrice) buyPrice.textContent = dot.dataset.price;
        if (variantInput) variantInput.value = dot.dataset.variantId;
        if (linkEl) {
          const url = new URL(linkEl.href, window.location.origin);
          url.searchParams.set('variant', dot.dataset.variantId);
          linkEl.href = url.pathname + url.search;
          linkEl.setAttribute('aria-label', `${linkEl.dataset.title} — ${dot.dataset.name}`);
        }

        // The colourway name takes on the colourway's colour. Always with a
        // fallback: an unset custom property resolves to nothing at all, and
        // the name then silently inherits body colour.
        const accent = dot.dataset.accent;
        if (accent) root.style.setProperty('--lens', accent);

        autoScaleName(dot.dataset.name);
      };

      /* The colourway name is set at clamp(34px,6.6vw,96px). "Transformative
         Teal" is twice the length of "Black" and overflows its column, so
         anything with a word longer than 7 characters is scaled down against
         its longest word. docs/01 §3. */
      const autoScaleName = (name) => {
        if (!nameEl || !name) return;
        const longest = name.split(/\s+/).reduce((a, w) => Math.max(a, w.length), 0);
        if (longest > 7) {
          nameEl.style.fontSize = `clamp(28px, ${((6.6 * 7) / longest).toFixed(2)}vw, ${Math.round((96 * 7) / longest)}px)`;
          nameEl.style.maxWidth = `${Math.max(7, longest)}ch`;
        } else {
          nameEl.style.fontSize = '';
          nameEl.style.maxWidth = '';
        }
      };

      const onDot = (e) => {
        const i = parseInt(e.currentTarget.dataset.lensDot, 10);
        setColour(i);
      };

      dots.forEach((d) => d.addEventListener('click', onDot));

      /* Hide the floating discount badge while this block is on screen, or it
         lands on top of the lens dots and buy button. */
      let io = null;
      if (stick && 'IntersectionObserver' in window) {
        io = new IntersectionObserver(
          ([entry]) => document.body.classList.toggle('lens-on', entry.isIntersecting),
          { threshold: 0.5 }
        );
        io.observe(stick);
      }

      if (buyLabel) buyLabel.dataset.prefix = buyLabel.textContent.trim().split(' ')[0];

      setColour(0);

      return { dots, onDot, io };
    },

    unmount(root, s) {
      if (!s) return;
      s.dots.forEach((d) => d.removeEventListener('click', s.onDot));
      s.io?.disconnect();
      document.body.classList.remove('lens-on');
    },
  });
})();
